import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Volume2, Maximize, Heart } from 'lucide-react';

interface VideoSectionProps {
    title: string;
    subtitle: string;
    videoSource: string;
}

const VideoSection: React.FC<VideoSectionProps> = ({ title, subtitle, videoSource }) => {
    const [isPlaying, setIsPlaying] = useState(false);

    return (
        <div className="px-6 py-6 flex flex-col items-center">
            <div className="text-center mb-8">
                <h2 className="text-3xl font-playfair font-bold text-primary-red mb-2">{title}</h2>
                <p className="text-gray-500 dark:text-gray-400 font-poppins text-sm">{subtitle}</p>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="max-w-6xl w-full bg-black rounded-[3rem] overflow-hidden shadow-2xl relative aspect-video border-8 border-white dark:border-gray-800"
            >
                <video
                    src={videoSource}
                    className="w-full h-full object-cover opacity-80"
                    controls={isPlaying}
                    autoPlay={false}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                />

                {!isPlaying && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[2px]">
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => setIsPlaying(true)}
                            className="w-24 h-24 rounded-full bg-primary-red text-white flex items-center justify-center shadow-2xl relative group"
                        >
                            <div className="absolute inset-0 rounded-full border-4 border-white m-1 group-hover:m-0 transition-all" />
                            <Play size={40} className="ml-1" fill="currentColor" />
                        </motion.button>
                        <p className="mt-6 text-white font-bold font-poppins uppercase tracking-widest drop-shadow-md flex items-center space-x-2">
                            <span>Watch Our Story</span>
                            <Heart size={16} fill="currentColor" />
                        </p>
                    </div>
                )}

                {/* Video Overlays (when not playing) */}
                {!isPlaying && (
                    <>
                        <div className="absolute top-8 left-8 flex items-center space-x-3 text-white">
                            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                                <Volume2 size={18} />
                            </div>
                            <span className="font-bold text-sm tracking-widest uppercase">HD Audio</span>
                        </div>
                        <div className="absolute bottom-8 right-8 text-white/50">
                            <Maximize size={24} />
                        </div>
                    </>
                )}
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.5 }}
                className="mt-8 flex items-center space-x-3 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px]"
            >
                <div className="h-0.5 w-12 bg-gray-200 dark:bg-gray-800" />
                <span>A cinematic look into our love</span>
                <div className="h-0.5 w-12 bg-gray-200 dark:bg-gray-800" />
            </motion.div>
        </div>
    );
};

export default VideoSection;
